import { ReduxPageType } from '@common-types/ReduxPage';
import { Routes } from '@common/constants';
import { TranslationsFunc } from '@lang';
import { Item } from 'shared-types';
import { BasketDetails } from 'shared-types/Basket';
import { separateAndGroupPackageAndNonPackageItems } from '../ItemListingRows/helpers/separateAndGroupPackageAndNonPackageItems';
import { ModuleType } from './BasketListing.types';

const MODULE_LABEL_KEYS = {
  rooms: 'basketDrawer.rooms',
  tables: 'basketDrawer.dining',
  spa: 'basketDrawer.spa',
  golf: 'basketDrawer.golf',
} as const;

export const getModuleLabel = (
  module: string,
  labels: Record<string, string | undefined>,
  t: TranslationsFunc
): string | undefined =>
  module in MODULE_LABEL_KEYS
    ? labels[module] || t(MODULE_LABEL_KEYS[module as keyof typeof MODULE_LABEL_KEYS])
    : undefined;

type ReduxProps = {
  spaPage: ReduxPageType;
  tablesPage: ReduxPageType;
  roomsPage: ReduxPageType;
  golfPage?: ReduxPageType;
};

export const groupByType = (basketItems: NonNullable<BasketDetails['items']>) =>
  basketItems.reduce((itemsMap, item) => {
    const type = item?.type?.startsWith('spa') ? 'spa' : item.type;
    return {
      ...itemsMap,
      [String(type)]: [...(itemsMap[String(type)] || []), item],
    };
  }, {});

export const pluralizeAdults = (adults: number, t: TranslationsFunc) => {
  return adults === 1
    ? t('basketDrawer.helpers.pluraliseAdults.singular')
    : t('basketDrawer.helpers.pluraliseAdults.plural');
};

export const pluralizeGuests = (guests: number, t: TranslationsFunc) => {
  return guests === 1
    ? t('basketDrawer.numberOfGuests.singular')
    : t('basketDrawer.numberOfGuests.plural');
};

export const pluralizeChildren = (children: number, t: TranslationsFunc) => {
  return children === 1
    ? t('basketDrawer.helpers.pluraliseChildren.singular')
    : t('basketDrawer.helpers.pluraliseChildren.plural');
};

export const calculateTotalNumberOfItemsInBasket = (items: NonNullable<BasketDetails['items']>) => {
  const groupedItemsByType = separateAndGroupPackageAndNonPackageItems(items, groupByType);
  return Object.values(groupedItemsByType).reduce((acc, items) => acc + items.length, 0);
};

export const mapModules = (
  modules: string[],
  { spaPage, tablesPage, roomsPage, golfPage }: ReduxProps
) => {
  return modules
    .map((m) => {
      if (m === 'spa' && spaPage) {
        return {
          ...spaPage,
          route: Routes.SPA_HOME,
        };
      }
      if (m === 'golf' && golfPage) {
        return {
          ...golfPage,
          route: Routes.GOLF_HOME,
        };
      }
      if (m === 'tables' && tablesPage) {
        return { ...tablesPage, route: Routes.TABLES_HOME, slug: 'dining' };
      }
      if (m === 'rooms' && roomsPage) {
        return {
          ...roomsPage,
          route: Routes.ROOM_LIST,
        };
      }
      return null;
    })
    .filter(Boolean);
};

type PackageBasketItem = Pick<Item, 'id' | 'type' | 'dynamicPackage' | 'packageBasketItemId'> &
  Partial<Pick<Item, 'bookable' | 'checkIn' | 'checkOut'>>;

const PACKAGE_MODULES: ModuleType[] = ['rooms', 'spa', 'tables'];

const BOOKED_ONCE_PER_BOOKING: ModuleType[] = ['tables'];

const toPackageModule = (type?: string): ModuleType | undefined => {
  if (type === 'rooms' || type === 'tables') return type;
  if (type?.startsWith('spa-')) return 'spa';
  return undefined;
};

const toBookingKey = (parent: PackageBasketItem): string => {
  const rateId = parent.bookable?.ids?.rateId;
  return rateId ? [rateId, parent.checkIn, parent.checkOut].join('|') : `item:${parent.id}`;
};

export const getModulesEveryPackageIncludes = (items: PackageBasketItem[]): ModuleType[] => {
  const basketItems = Array.isArray(items) ? items : [];
  const parents = basketItems.filter((item) => item.dynamicPackage === 'parent');
  if (parents.length === 0) return [];

  const rooms = parents.map((parent) => ({
    booking: toBookingKey(parent),
    modules: new Set(
      basketItems
        .filter(
          (item) => item === parent || String(item.packageBasketItemId ?? '') === String(parent.id)
        )
        .map((item) => toPackageModule(item.type))
    ),
  }));

  const isInBooking = (module: ModuleType, booking: string) =>
    BOOKED_ONCE_PER_BOOKING.includes(module) &&
    rooms.some((room) => room.booking === booking && room.modules.has(module));

  return PACKAGE_MODULES.filter((module) =>
    rooms.every(({ booking, modules }) => modules.has(module) || isInBooking(module, booking))
  );
};
